import React from 'react';
import { useTheme } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import DSBox from '../../../design-system/DSBox';
import DSGradientViewPaper from '../../../design-system/Paper/DSGradientViewPaper';
import DSCircularProgress from '../../../design-system/DSCircularProgress';
import { DSTypography } from '../../../design-system/Typography';
import CalendarEventWidgetItem from './CalendarEventWidgetItem';
import CompactCountryDropdown from '../../../components/CountryDropdown/components/CompactCountryDropdown';
import { useCalendarEventWidget } from '../hooks/useCalendarEventWidget';
import { ReactComponent as RightIcon } from '../../../design-system/Icons/RightIcon.svg';

const CalendarEventWidget = () => {
  const { spacing, palette } = useTheme();
  const navigate = useNavigate();
  const { nextCalendarEvents, isLoading, selectedCountry, onCountryChange } =
    useCalendarEventWidget();

  const EmptyState = () => {
    return (
      <DSBox
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100%',
          width: '100%',
        }}
      >
        <DSTypography
          variant="body2"
          sx={{
            color: palette.action.disabled,
            textAlign: 'center',
          }}
        >
          No upcoming calendar events found
        </DSTypography>
      </DSBox>
    );
  };

  const LoadingState = () => {
    return (
      <DSBox
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100%',
          width: '100%',
        }}
      >
        <DSCircularProgress size={24} />
      </DSBox>
    );
  };

  return (
    <DSGradientViewPaper
      sx={{
        flexDirection: 'column',
        padding: spacing(2),
        gap: spacing(1),
        width: '100%',
      }}
    >
      <DSBox
        sx={{
          display: 'flex',
          width: '100%',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <DSTypography variant="h6">The Next Events</DSTypography>
        <CompactCountryDropdown
          selectedCountry={selectedCountry}
          onCountryChange={onCountryChange}
        />
      </DSBox>

      <DSBox
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: spacing(1.5),
          flex: 1,
          overflow: 'hidden',
          width: '100%',
        }}
      >
        {isLoading && <LoadingState />}
        {nextCalendarEvents?.length === 0 && !isLoading && <EmptyState />}
        {nextCalendarEvents?.map((calendarEvent) => (
          <CalendarEventWidgetItem
            key={calendarEvent.id}
            calendarEvent={calendarEvent}
          />
        ))}
      </DSBox>

      {nextCalendarEvents?.length > 0 && (
        <DSBox
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
          }}
        >
          <DSBox
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: spacing(1),
              cursor: 'pointer',
              '&:hover': {
                '& .MuiTypography-root': {
                  color: palette.text.primary,
                },
                '& svg': {
                  color: palette.text.primary,
                },
              },
            }}
            onClick={() => navigate('/calendar-events')}
          >
            <DSTypography
              variant="value"
              sx={{
                color: palette.action.active,
              }}
            >
              More Events
            </DSTypography>

            <RightIcon color={palette.action.active} width={spacing(2)} />
          </DSBox>
        </DSBox>
      )}
    </DSGradientViewPaper>
  );
};

export default CalendarEventWidget;
